import React from 'react';
import classNames from 'classnames';
import striptags from 'striptags';

import styled from '@benzinga/themetron';
import { Button, ButtonVariant, Card, Icon } from '@benzinga/core-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatDatePretty, formatDurationPretty } from '@benzinga/date-utils';
import { BzImage } from '@benzinga/image';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { PodcastProvidersUrls } from '@benzinga/content-manager';
import { convertToRelativeUrl } from '@benzinga/utils';
import { PodcastPlatformBadges } from '../PodcastPlatformBadges';

export type PodcastCardVariant = 'default' | 'horizontal';
export interface PodcastCardData {
  id: number;
  date: string;
  description: string;
  image: string;
  slug: string;
  title: string;
  show_name: string;
  link: string;
  podcast_page_link: string;
  podcast_providers_urls: PodcastProvidersUrls;
  duration?: number | string;
}

export interface PodcastCardProps {
  className?: string;
  data: PodcastCardData;
  preload?: boolean;
  variant?: PodcastCardVariant;
  actionButtonClassname?: string;
  actionButtonText?: string;
  actionButtonVariant?: ButtonVariant;
  showPodcastPlatformBadges?: boolean;
  openInNewTab?: boolean;
}

export const PodcastCard: React.FC<PodcastCardProps> = ({
  actionButtonClassname,
  actionButtonText,
  actionButtonVariant,
  className,
  data,
  openInNewTab = false,
  preload = false,
  showPodcastPlatformBadges = false,
  variant = 'default',
}) => {
  const { date, description, duration, image, link, podcast_page_link, show_name, title } = data;

  const href = convertToRelativeUrl(podcast_page_link || link || '');

  if (variant === 'horizontal') {
    const platforms = {
      'apple-podcasts': {
        name: 'Apple Podcasts',
        url: data.podcast_providers_urls?.itunes || null,
      },
      'google-podcasts': {
        name: 'Podcasts',
        url: data.podcast_providers_urls?.google_podcasts || null,
      },
      spotify: {
        name: 'Spotify',
        url: data.podcast_providers_urls?.spotify || null,
      },
      stitcher: {
        name: 'Stitcher',
        url: data.podcast_providers_urls?.stitcher || null,
      },
      // podbean: {
      //   name: 'Podbean',
      //   url: data.podcast_providers_urls?.podbean || null,
      // },
      tunein: {
        name: 'TuneIn',
        url: data.podcast_providers_urls?.tune_in || null,
      },

      youtube: {
        name: 'YouTube',
        url: data.podcast_providers_urls?.youtube || null,
      },
    };

    const isMissingUrls = Object.values(platforms).filter(key => key.url).length === 0;

    return (
      <PodcastCardHorizontal className={`podcast-card horizontal p-4`}>
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a className="podcast-card-top flex gap-4 w-full" href={href} target={openInNewTab ? '_blank' : undefined}>
          <div className="podcast-card-image-container">
            <Thumbnail className="podcast-card-image-wrapper">
              {image && (
                <BzImage
                  alt=""
                  blurDataURL={`${image}?optimize=medium&dpr=1&auto=webp&width=30`}
                  blurOpacity={10}
                  height="100%"
                  layout="fill"
                  loaderProps={{ width: 350 }}
                  loading="lazy"
                  objectFit="contain"
                  placeholder="blur"
                  src={image}
                  width="100%"
                />
              )}
              {/* {image && <NextImage alt="" height="100%" layout="fill" objectFit="contain" src={image} width="100%" />} */}
            </Thumbnail>
          </div>
          <CardInfo>
            {title && <h4 className="podcast-card-title" dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />}
            {description && (
              <p
                className="podcast-card-description"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(striptags(description)) }}
              />
            )}
            <div className="flex justify-between items-center mt-4 w-full">
              <span className="flex items-center">
                <Button className={actionButtonClassname} variant={actionButtonVariant ?? 'white-outline'}>
                  {actionButtonText ?? 'Listen Now'}
                </Button>
                {duration !== 0 && (
                  <span className="duration px-2">
                    {typeof duration === 'number' ? formatDurationPretty(duration / 1000) : duration || '-'}
                  </span>
                )}
              </span>
              {date && (
                <span className="date px-2">
                  {formatDatePretty(date, { emitCurrentYear: true, timePeriod: { month: 'short' } })}
                </span>
              )}
            </div>
          </CardInfo>
        </a>
        {showPodcastPlatformBadges && !isMissingUrls && (
          <div className="podcast-card-bottom w-full flex flex-col sm:flex-row items-center justify-center sm:justify-between pt-3 gap-4">
            <span className="text-gray-400 text-sm">Available on platforms: </span>
            <PodcastPlatformBadges hideName={true} platforms={platforms} />
          </div>
        )}
      </PodcastCardHorizontal>
    );
  }

  return (
    <PodcastCardDefault
      as={href ? 'a' : 'div'}
      className={classNames('flex flex-col podcast-card default', { [`${className}`]: !!className })}
      href={href}
      target={openInNewTab ? '_blank' : undefined}
    >
      <div className="card-top">
        <div className="w-full h-full relative">
          {image && (
            <BzImage
              alt=""
              blurDataURL={`${image}?optimize=medium&dpr=1&auto=webp&width=30`}
              blurOpacity={10}
              height="100%"
              layout="fill"
              loaderProps={{ width: 340 }}
              loading="lazy"
              objectFit="cover"
              placeholder="blur"
              preload={preload}
              sizes="(max-width: 800px) 320px, (min-width: 800px) 320px"
              src={image}
            />
          )}
        </div>
      </div>
      <div className="card-bottom flex flex-col grow">
        <div className="flex justify-between w-full text-blue-700 font-semibold mb-1">
          {show_name && <span>{show_name}</span>}
          <span>{date ? formatDatePretty(date, { emitYear: true }) : '-'}</span>
        </div>
        <div className="flex w-full mb-2 text-gray-700">
          {title && (
            <h4
              className="podcast-card-title text-black text-lg"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }}
            />
          )}
        </div>
        {description && (
          <p
            className="podcast-card-description text-sm text-gray-500"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(striptags(description)) }}
          />
        )}
        <Button className="listen-now-button mt-auto w-full" variant="flat-light-blue-outlined">
          <div className="flex items-center justify-between">
            <div className="bg-blue-500 p-1 rounded-full flex items-center justify-center w-5 h-5 mr-1">
              <Icon className="play-icon text-blue-100" icon={faPlay} />
            </div>
            LISTEN NOW
          </div>
          <span className="duration-text">
            {' '}
            {typeof duration === 'number' ? formatDurationPretty(duration / 1000) : duration || '-'}
          </span>
        </Button>
      </div>
    </PodcastCardDefault>
  );
};

const PodcastCardDefault = styled(Card)`
  display: flex;
  max-width: 320px;
  width: 100%;
  padding: unset;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  overflow: hidden;
  transition: box-shadow 0.3s linear;

  &:hover {
    box-shadow: 0 2px 16px rgba(25, 68, 128, 0.1);
  }

  .card-top {
    height: 290px;
    display: flex;
    flex-direction: column;
    background-image: linear-gradient(
      205deg,
      hsl(215deg 54% 27%) 0%,
      hsl(215deg 55% 25%) 7%,
      hsl(214deg 56% 23%) 13%,
      hsl(214deg 57% 21%) 20%,
      hsl(214deg 59% 19%) 28%,
      hsl(214deg 60% 16%) 36%,
      hsl(214deg 62% 14%) 44%,
      hsl(214deg 65% 12%) 54%,
      hsl(214deg 67% 10%) 67%,
      hsl(215deg 72% 8%) 100%
    );
  }

  .podcast-card-title {
    -webkit-line-clamp: 3;
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  .podcast-card-description {
    -webkit-line-clamp: 4;
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    margin-bottom: 0.5rem;
  }

  .card-bottom {
    padding: 1rem;
  }

  .listen-now-button {
    padding: 5px 10px;

    .play-icon {
      font-size: 10px;
    }

    .duration-text {
      line-height: 1.5;
    }

    > span {
      display: flex;
      justify-content: space-between;
      width: 100%;
    }
  }
`;

const PodcastCardHorizontal = styled.div`
  &.podcast-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    border: 1px solid ${({ theme }) => theme.colors.border};
    min-height: 180px;
    width: 520px;
    max-width: 340px;
    background-image: linear-gradient(
      90deg,
      hsl(216deg 37% 11%) 0%,
      hsl(216deg 37% 11%) 6%,
      hsl(216deg 37% 12%) 12%,
      hsl(216deg 36% 12%) 18%,
      hsl(216deg 36% 13%) 23%,
      hsl(216deg 36% 13%) 29%,
      hsl(216deg 36% 14%) 34%,
      hsl(216deg 36% 15%) 39%,
      hsl(216deg 36% 15%) 43%,
      hsl(216deg 35% 16%) 48%,
      hsl(216deg 35% 16%) 52%,
      hsl(216deg 35% 17%) 56%,
      hsl(216deg 35% 18%) 60%,
      hsl(216deg 35% 18%) 64%,
      hsl(216deg 35% 19%) 67%,
      hsl(216deg 35% 20%) 71%,
      hsl(216deg 35% 20%) 74%,
      hsl(216deg 35% 21%) 78%,
      hsl(216deg 35% 21%) 81%,
      hsl(216deg 35% 22%) 86%,
      hsl(216deg 34% 23%) 100%
    );
    transition: box-shadow 0.4s linear;

    .podcast-card-top {
      flex-direction: column;
    }

    .podcast-card-image-container {
      width: 100%;
    }
    .podcast-card-image-wrapper {
      height: 290px;
      width: 100%;
    }

    .podcast-card-bottom {
      border-top: 1px solid ${({ theme }) => theme.colorPalette.gray600};
      margin-top: 1rem;
    }

    .podcast-platform-badge {
      height: 40px;
      width: 40px;
      padding: 0;
      border-radius: ${({ theme }) => theme.borderRadius.full};
      justify-content: center;
    }

    .podcast-platform-badge-icon {
      margin-right: 0;
    }

    @media (min-width: 460px) {
      min-width: 460px;
      max-width: unset;
      .podcast-card-top {
        flex-direction: row;
      }
      .podcast-card-image-container {
        width: unset;
      }
      .podcast-card-image-wrapper {
        height: 160px;
        width: 160px;
      }
    }

    &:hover {
      box-shadow: 0 2px 16px rgba(57, 76, 105, 0.5);
    }
  }
`;

const Thumbnail = styled.div`
  position: relative;
  height: 160px;
  width: 160px;
  overflow: hidden;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 0 1rem;
  color: #ffffff;
  h4 {
    font-weight: bold;
    color: #ffffff;
    margin-bottom: 0.25rem;
  }
  p {
    color: ${({ theme }) => theme.colorPalette.gray400};
    font-weight: 300;
  }
  .podcast-card-description {
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }
`;
